import React, { FC, useCallback } from 'react';
import "./LightSearchPreview.css"
import MarkdownPreview from "../MarkdownPreview/MarkdownPreview";
import BoardIcon from "../../../assets/board_frame.svg";
import { getFileUrlFromId } from "../../../helpers/getFileUrlFromId";
import WidgetContainer from "../../UiLib/WidgetContainer/WidgetContainer";
import { ISearchBoardResult, ISearchProjectResult, ISearchResult, ISearchTaskResult } from "../../../common/api_interfaces/search/ISearchResult";
import { useNavigate } from "react-router-dom";

export interface ILightSearchPreview {
    callBack?: (show: boolean) => void;
    dataForView?: ISearchResult;
}

function truncateTextWithFoundEntries(text: string, maxLength: number = 80) {
    // Удаляем все теги кроме found-entry
    const cleanText = text.replace(/<(?!(?:\/)?found-entry\b).*?>/g, '');

    // Если текст и так короче лимита, возвращаем как есть
    if (cleanText.length <= maxLength) {
        return cleanText;
    }

    // Находим все вхождения <found-entry>
    const foundEntries = [];
    const regex = /<found-entry>(.*?)<\/found-entry>/g;
    let match;

    while ((match = regex.exec(cleanText)) !== null) {
        foundEntries.push({
            fullTag: match[0],
            content: match[1],
            start: match.index,
            end: match.index + match[0].length,
            position: match.index
        });
    }

    // Если нет found-entry, просто обрезаем текст
    if (foundEntries.length === 0) {
        return cleanText.substring(0, maxLength - 3) + '...';
    }

    // Создаем массив для частей результата
    const resultParts = [];
    let totalTextLength = 0;

    // Обрабатываем каждый found-entry
    for (let i = 0; i < foundEntries.length; i++) {
        const entry = foundEntries[i];

        // Получаем текст до и после found-entry
        const textBefore = i === 0 ? cleanText.substring(0, entry.start) : cleanText.substring(foundEntries[i - 1].end, entry.start);
        const textAfter = cleanText.substring(foundEntries[i].end, foundEntries[i + 1]?.start ?? cleanText.length);

        // Получаем слова вокруг
        const wordsBefore = textBefore.trim().split(/\s+/).filter(w => w.length > 0);
        const wordsAfter = textAfter.trim().split(/\s+/).filter(w => w.length > 0);

        // Выбираем 2 слова до и 2 слова после (если есть)
        let contextBefore = '';
        let contextAfter = '';

        if (wordsBefore.length >= 2) {
            contextBefore = '...' + wordsBefore.slice(-2).join(' ');
        } else if (wordsBefore.length > 0) {
            contextBefore = wordsBefore.join(' ') + ' ';
        }

        if (wordsAfter.length >= 2) {
            contextAfter = wordsAfter.slice(0, 2).join(' ') + '... ';
        } else if (wordsAfter.length > 0) {
            contextAfter = wordsAfter.join(' ') + ' ';
        }

        // Проверяем, помещается ли в лимит
        const entryLength = entry.content.length;
        const contextLength = contextBefore.length + contextAfter.length;
        const totalNeeded = totalTextLength + contextLength + entryLength;

        if (totalNeeded <= maxLength) {
            if (contextBefore && (i === 0 || !resultParts[resultParts.length - 1].endsWith(contextBefore.trim()))) {
                resultParts.push(contextBefore);
                totalTextLength += contextBefore.length;
            }

            resultParts.push(entry.fullTag);
            totalTextLength += entryLength;

            if (contextAfter) {
                resultParts.push(contextAfter);
                totalTextLength += contextAfter.length;
            }
        } else {
            // Если не помещается, добавляем только found-entry
            const remaining = maxLength - totalTextLength;
            if (remaining >= entryLength + 3) {
                resultParts.push(entry.fullTag + "...");
                totalTextLength += entryLength;
            }
            break;
        }
    }

    let result = resultParts.join('');

    return result;
}

const LightSearchPreview: FC<ILightSearchPreview> = ({ dataForView, callBack }) => {
    const navigate = useNavigate();

    const handleItemClick = useCallback(() => {
        callBack?.(false);
    }, [callBack]);

    const taskConstructor = useCallback((taskData: ISearchTaskResult) => {
        const handleTaskClick = () => {
            handleItemClick();
            navigate(`/task/${taskData.id}`);
        };

        const str = taskData.whatFound?.str ?? "";
        const truncatedStr = truncateTextWithFoundEntries(str, 80);

        return (
            <div
                onClick={handleTaskClick}
                id={`search_result_component-task-${taskData.id}`}
                className="search_result_component-task"
                key={`task-${taskData.id}`}
            >
                <div className="search_result_component-number">
                    <span>{taskData.taskNumber}</span>
                </div>
                <div className="search_result_component-task-name">
                    <span>{taskData.name}</span>
                </div>
                <div className="search_result_component-task-description">
                    <MarkdownPreview content={truncatedStr} />
                </div>
            </div>
        );
    }, [handleItemClick, navigate]);

    const boardConstructor = useCallback((boardData: ISearchBoardResult) => {
        const handleBoardClick = () => {
            handleItemClick();
            navigate(`/board/${boardData.id}`);
        };

        const truncatedStr = truncateTextWithFoundEntries(boardData.whatFound?.str ?? "", 80);

        return (
            <div
                onClick={handleBoardClick}
                id={`search_result_component-board-${boardData.id}`}
                className="search_result_component-board"
                key={`board-${boardData.id}`}
            >
                <img
                    src={BoardIcon}
                    className="board-icon"
                    alt="board-icon"
                />
                <MarkdownPreview content={truncatedStr} />
            </div>
        );
    }, [handleItemClick, navigate]);

    const projectConstructor = useCallback((projectData: ISearchProjectResult) => {
        const handleProjectClick = () => {
            handleItemClick();
            navigate(`/project/${projectData.id}`);
        };

        const str = projectData.whatFound?.str ?? "";
        const photoUrl = projectData.photoId ? getFileUrlFromId(projectData.photoId) : undefined;
        const truncatedStr = truncateTextWithFoundEntries(str, 80);

        return (
            <div
                onClick={handleProjectClick}
                id={`search_result_component-project-${projectData.id}`}
                key={`project-${projectData.id}`}
            >
                <div className="search_result_component-project">
                    <div>
                        {photoUrl && (
                            <img src={photoUrl} alt="project" />
                        )}
                    </div>
                    <div>
                        <div className="search_result_component-number">
                            <span>{projectData.projectNumber}</span>
                        </div>
                        <div className="search_result_component-task-name">
                            <span>{projectData.name}</span>
                        </div>
                        <div className="search_result_component-description">
                            <MarkdownPreview content={truncatedStr} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [handleItemClick, navigate]);

    const containerStyle = {
        marginTop: '0px',
        padding: "0px",
        borderRadius: "0px",
        boxSizing: 'unset' as const,
        maxHeight: "480px",
        overflowY: "auto" as const,
        paddingRight: "2px"
    };

    return (
        <WidgetContainer style={containerStyle}>
            <div className="search_result_component">
                <div className="search_result_component_head">
                    <span>Задачи - {dataForView?.tasks?.length ?? 0}</span>
                </div>
                <div className="search_result_component_body">
                    {dataForView?.tasks?.map(taskConstructor)}
                </div>
            </div>
            <div className="search_result_component">
                <div className="search_result_component_head">
                    <span>Доски - {dataForView?.boards?.length ?? 0}</span>
                </div>
                <div className="search_result_component_body">
                    {dataForView?.boards?.map(boardConstructor)}
                </div>
            </div>
            <div className="search_result_component">
                <div className="search_result_component_head">
                    Проекты - {dataForView?.projects?.length ?? 0}
                </div>
                <div className="search_result_component_body">
                    {dataForView?.projects?.map(projectConstructor)}
                </div>
            </div>
        </WidgetContainer>
    );
};

export default React.memo(LightSearchPreview);